import {Injectable} from "@angular/core";
import {ImageCache} from "../caches/image-cache";
import {CanvasCache, CanvasType} from "../caches/canvas-cache";
import {calculatePerspectiveShiftProperties, getLightPositionAccordingToFlip} from "../helper";
import {WebGLService} from "../webgl/web-gl.service";
import {ScenarioRenderer} from "./scenario-renderer";
import {ClippedCorners, Corners, LightPosition, Product, ProductsAtIndex, RenderOptions} from "../types";

export interface VisualDoorRenderProperties {
  renderingContext: CanvasRenderingContext2D
  corners: Corners
  products?: ProductsAtIndex
  scenario: {
    lightPosition: LightPosition
  }
}

@Injectable({
  providedIn: 'root'
})
export class VisualDoorRenderer {

  constructor(
    private readonly imageCache: ImageCache,
    private readonly canvasCache: CanvasCache,
    private readonly webGLService: WebGLService
  ) {
  }

  public async renderVisualDoor(
    {renderingContext, corners, products, scenario}: VisualDoorRenderProperties,
    {drawGlassLayer, flipDoor, enabledShadowProfiles}: RenderOptions
  ): Promise<void> {
    // Stores the initial state of the canvas (before clipping)
    renderingContext.save();

    // Clip the canvas to the door's shape for all further drawings for this door
    console.time('- Clip corners')
    this.clipCorners({renderingContext, corners})
    console.timeEnd('- Clip corners')

    // Draw glass layer
    if (drawGlassLayer) {
      console.time('- Draw glass layer')
      this.drawGlassLayer({
        corners,
        renderingContext,
      })
      console.timeEnd('- Draw glass layer')
    }

    // Draw doors or products
    console.group(`- Draw products on canvas`)
    console.time('- All drawing')
    await this.renderProducts({
      renderingContext,
      corners,
      lightPosition: scenario.lightPosition,
      products,
      flipDoor,
      enabledShadowProfiles,
    })
    console.timeEnd('- All drawing')
    console.groupEnd()

    renderingContext.restore();
  }

  private async renderProducts({
                                 renderingContext,
                                 lightPosition,
                                 corners,
                                 products,
                                 flipDoor,
                                 enabledShadowProfiles
                               }: {
    renderingContext: CanvasRenderingContext2D
    products?: ProductsAtIndex,
    flipDoor?: boolean,
    corners: Corners & ClippedCorners,
    lightPosition: LightPosition
    enabledShadowProfiles?: boolean
  }) {

    console.time('- Filter products')
    let allProducts = [...products?.products || [], products?.door].filter(p => !!p) as Array<Product>

    // Filter out products without image and sort by layer
    allProducts = allProducts
      .filter(p => !!p.visualisationImageUrl)
      .sort((a, b) => {
        return (a?.visualisationLayer || 0) - (b.visualisationLayer || 0)
      })

    console.timeEnd('- Filter products')

    let index = 1;
    for (const product of allProducts) {
      console.groupCollapsed(`- Drawing: ${index}. ${product.productType} - ${product.name}`)
      await this.renderProduct({renderingContext, product, corners, flipDoor, enabledShadowProfiles, lightPosition})
      console.groupEnd()
      index++;
    }
  }

  private async renderProduct({renderingContext, product, corners, flipDoor, enabledShadowProfiles, lightPosition}: {
    renderingContext: CanvasRenderingContext2D,
    product: Product,
    corners: Corners & ClippedCorners,
    flipDoor?: boolean
    enabledShadowProfiles?: boolean
    lightPosition: LightPosition
  }): Promise<void> {
    console.time(`- All`)

    renderingContext.save();

    // Load image
    console.time(`- load image`)
    const image = await this.imageCache.get({url: product.visualisationImageUrl!})
    console.timeEnd(`- load image`)

    console.time(`- perspective shift`)
    const door = this.perspectiveShift({doorCorners: corners, image, flipDoor: !!flipDoor});
    console.timeEnd(`- perspective shift`)

    console.time(`- draw image`)
    renderingContext.drawImage(door, 0, 0)
    door.remove();
    console.timeEnd(`- draw image`)

    // Draw the shadow image
    if (enabledShadowProfiles && product.shadowImageUrls) {
      console.time(`- draw shadow profile`)
      const correctLightPosition = getLightPositionAccordingToFlip({lightPosition, flipped: !!flipDoor})
      const imageUrl = product.shadowImageUrls.get(correctLightPosition)

      if (imageUrl) {
        renderingContext.globalAlpha = 0.6;
        const loadedImage = await this.imageCache.get({url: imageUrl})
        const shadowProfile = this.perspectiveShift({doorCorners: corners, image: loadedImage, flipDoor});
        renderingContext.drawImage(shadowProfile, 0, 0)
        shadowProfile.remove();
        renderingContext.globalAlpha = 1;
      }
      console.timeEnd(`- draw shadow profile`)
    }

    console.timeEnd(`- All`)

    // Restore the context to its original state
    renderingContext.restore();
  }

  /**
   * Clips the rendering context to the corners of the door
   * Followed drawing will only be visible within the clipped area
   */
  private clipCorners({renderingContext, corners}: {
    renderingContext: CanvasRenderingContext2D,
    corners: Corners & ClippedCorners
  }) {
    ScenarioRenderer.drawCornersPath({renderingContext, corners})
    renderingContext.clip();
  }

  private drawGlassLayer({
                           renderingContext,
                           corners,
                           fillStyle,
                           filter
                         }: {
    renderingContext: CanvasRenderingContext2D
    corners: Corners & ClippedCorners
    fillStyle?: string
    filter?: string
  }): void {

    ScenarioRenderer.drawCornersPath({
      renderingContext,
      corners
    })

    renderingContext.fillStyle = fillStyle || 'rgba(95, 95, 95, 0.3)';
    renderingContext.filter = filter || 'blur(5px)';

    // Fill the entire canvas with the semi-transparent color
    renderingContext.fill()
    renderingContext.drawImage(renderingContext.canvas, 0, 0);

    // Reset styles
    renderingContext.filter = 'none';
  }

  /**
   * Performs the perspective shifting of the visual door according to the door corners in the user scenario
   */
  private perspectiveShift({doorCorners, image, flipDoor}: {
    doorCorners: Corners,
    image: HTMLImageElement,
    flipDoor?: boolean
  }): HTMLCanvasElement {
    const webGLCanvas = this.webGLService.getWebGLCanvas()

    const perspectiveShiftProperties = calculatePerspectiveShiftProperties({
      doorCorners,
      imageSize: {
        width: image.width,
        height: image.height
      }
    });

    // Create a canvas on which the visual door transformation takes place
    const {
      context: textureCanvasContext,
      canvas: textureCanvas
    } = this.canvasCache.get(CanvasType.DOOR, {
      width: perspectiveShiftProperties.textureCanvasWidth,
      height: perspectiveShiftProperties.textureCanvasHeight
    })

    textureCanvasContext.save()

    if (flipDoor) {
      // Flip the canvas horizontally
      textureCanvasContext.scale(-1, 1);
    }

    textureCanvasContext.drawImage(image, flipDoor ? -image.width : 0, 0);
    textureCanvasContext.restore()

    const glfxTexture = webGLCanvas.texture(textureCanvas);

    // Draw the texture on the fxCanvas
    webGLCanvas.draw(glfxTexture);

    webGLCanvas.perspective(
      perspectiveShiftProperties.doorCornersOriginal,
      perspectiveShiftProperties.doorCornersTransformed
    );
    webGLCanvas.update();
    glfxTexture.destroy()

    return webGLCanvas.getHtmlCanvas()
  }
}
